/**
 * Soft-safe Move 3: Setup step panel.
 * Soft-refuse: identity lock held (no metric Soft UI copy). Soft 14 ops-only.
 * Poker 2·5: blinds Next-disable must not regress (areBlindsValid).
 */
import {
  t,
} from '../../i18n/strings'
import {
  getState,
  parseBlindInput,
  setAnte,
  setBlinds,
  setEffectiveStackBb,
  setHeroStackBb,
  setHeroStackChips,
  setPlayerCount,
  setPotBb,
  setStackUnit,
} from '../../state'
import {
  el,
} from '../dom'
import {
  renderTableViz,
} from '../tableViz'
import {
  POT_BB_MAJOR,
  POT_BB_STOPS,
  STACK_BB_MAJOR,
  STACK_BB_STOPS,
  customDetails,
  explainOnly,
  formatHintBb,
  parseOptionalNumber,
  snapSlider,
} from './helpers'


export function renderSetup(): HTMLElement {
  const ui = t()
  const { hand } = getState()
  const stackValue =
    hand.stackUnit === 'chips' ? hand.heroStackChips : hand.heroStackBb

  const stackCustomInput = el('input', {
    className: 'input',
    attrs: {
      type: 'number',
      min: 0,
      step: 'any',
      inputmode: 'decimal',
      value: stackValue,
      'aria-label':
        hand.stackUnit === 'chips' ? ui.stackChips : ui.stackBb,
    },
    on: {
      change: (e) => {
        const value = Number((e.target as HTMLInputElement).value)
        if (hand.stackUnit === 'chips') setHeroStackChips(value)
        else setHeroStackBb(value)
      },
    },
  })

  const formatStackPrimary = (n: number) => `${formatHintBb(n)}bb`

  const heroStackField =
    hand.stackUnit === 'chips'
      ? el('label', { className: 'field' }, [
          el('span', { className: 'label', text: ui.stackChips }),
          stackCustomInput,
        ])
      : el('div', { className: 'field' }, [
          el('span', { className: 'label', text: ui.stackBb }),
          snapSlider({
            stops: STACK_BB_STOPS,
            value:
              Number.isFinite(hand.heroStackBb) && hand.heroStackBb > 0
                ? hand.heroStackBb
                : 100,
            formatPrimary: formatStackPrimary,
            onCommit: setHeroStackBb,
            ariaLabel: ui.stackBb,
            hint: ui.snapHint,
            majorStops: STACK_BB_MAJOR,
          }),
          customDetails(ui.sizeCustomToggle, stackCustomInput),
        ])

  const effCurrent =
    hand.effectiveStackBb != null && Number.isFinite(hand.effectiveStackBb)
      ? hand.effectiveStackBb
      : null
  const effCustom = el('input', {
    className: 'input',
    attrs: {
      type: 'number',
      min: 0,
      step: 'any',
      inputmode: 'decimal',
      value: effCurrent ?? '',
      'aria-label': ui.effectiveStack,
    },
    on: {
      change: (e) => {
        setEffectiveStackBb(
          parseOptionalNumber((e.target as HTMLInputElement).value),
        )
      },
    },
  })

  const potCurrent =
    hand.potBb != null && Number.isFinite(hand.potBb) ? hand.potBb : null
  const potCustom = el('input', {
    className: 'input',
    attrs: {
      type: 'number',
      min: 0,
      step: 'any',
      inputmode: 'decimal',
      value: potCurrent ?? '',
      'aria-label': ui.potBb,
    },
    on: {
      change: (e) => {
        setPotBb(parseOptionalNumber((e.target as HTMLInputElement).value))
      },
    },
  })

  const hasPrecisionMeta =
    hand.stackUnit === 'chips' ||
    (hand.ante != null && Number.isFinite(hand.ante)) ||
    (hand.effectiveStackBb != null &&
      Number.isFinite(hand.effectiveStackBb)) ||
    (hand.potBb != null && Number.isFinite(hand.potBb))

  const stackUnitField = el('div', { className: 'field' }, [
    el('span', { className: 'label', text: ui.stackUnit }),
    el('div', { className: 'unitToggle' }, [
      el('button', {
        className: [
          'unitBtn',
          hand.stackUnit === 'bb' ? 'unitBtnActive' : '',
        ]
          .filter(Boolean)
          .join(' '),
        attrs: {
          type: 'button',
          'aria-pressed': hand.stackUnit === 'bb' ? 'true' : 'false',
        },
        text: ui.stackUnitBb,
        on: { click: () => setStackUnit('bb') },
      }),
      el('button', {
        className: [
          'unitBtn',
          hand.stackUnit === 'chips' ? 'unitBtnActive' : '',
        ]
          .filter(Boolean)
          .join(' '),
        attrs: {
          type: 'button',
          'aria-pressed': hand.stackUnit === 'chips' ? 'true' : 'false',
        },
        text: ui.stackUnitChips,
        on: { click: () => setStackUnit('chips') },
      }),
    ]),
  ])

  const precisionSummaryKids: HTMLElement[] = [
    el('span', {
      className: 'setupDetailsLabel',
      text: ui.setupDetailsToggle,
    }),
  ]
  if (hasPrecisionMeta) {
    precisionSummaryKids.push(
      el('span', {
        className: 'setupMetaChip',
        text: ui.setupMetaSetChip,
      }),
    )
  }

  const playersField = el('label', { className: 'field' }, [
    el('span', { className: 'label', text: ui.players }),
    (() => {
      const select = el('select', {
        className: 'select',
        on: {
          change: (e) => {
            setPlayerCount(Number((e.target as HTMLSelectElement).value))
          },
        },
      })
      for (const n of [2, 3, 4, 5, 6, 7, 8, 9]) {
        const opt = el('option', {
          text: ui.playersUnit(n),
          attrs: { value: n },
        })
        if (n === hand.playerCount) opt.selected = true
        select.append(opt)
      }
      return select
    })(),
  ])

  const precisionDetails = el('details', {
    className: 'customDetails setupDetails',
    // Stay collapsed by default — chip on summary shows when meta is set
  })
  precisionDetails.append(
    el(
      'summary',
      { className: 'customSummary setupDetailsSummary' },
      precisionSummaryKids,
    ),
    el('div', { className: 'customBody setupDetailsBody' }, [
      // Softwise M7: players Quiet — blinds is the one Setup L1 field.
      playersField,
      // Softwise M5: stack slider Quiet — table viz is L1; tap hint covers stack.
      heroStackField,
      stackUnitField,
      el('label', { className: 'field' }, [
        el('span', { className: 'label', text: ui.ante }),
        el('input', {
          className: 'input',
          attrs: {
            type: 'number',
            min: 0,
            step: 'any',
            inputmode: 'decimal',
            placeholder: ui.anteHint,
            value:
              hand.ante != null && Number.isFinite(hand.ante) ? hand.ante : '',
          },
          on: {
            change: (e) => {
              setAnte(parseOptionalNumber((e.target as HTMLInputElement).value))
            },
          },
        }),
      ]),
      el('div', { className: 'field' }, [
        el('span', { className: 'label', text: ui.effectiveStack }),
        snapSlider({
          stops: STACK_BB_STOPS,
          value: effCurrent,
          formatPrimary: formatStackPrimary,
          onCommit: setEffectiveStackBb,
          ariaLabel: ui.effectiveStack,
          hint: ui.snapHint,
          majorStops: STACK_BB_MAJOR,
          clear: {
            label: ui.clearOptional,
            onClear: () => setEffectiveStackBb(null),
            active: effCurrent == null,
          },
        }),
        customDetails(ui.sizeCustomToggle, effCustom),
      ]),
      el('div', { className: 'field' }, [
        el('span', { className: 'label', text: ui.potBb }),
        snapSlider({
          stops: POT_BB_STOPS,
          value: potCurrent,
          formatPrimary: formatStackPrimary,
          onCommit: setPotBb,
          ariaLabel: ui.potBb,
          hint: ui.snapHint,
          majorStops: POT_BB_MAJOR,
          clear: {
            label: ui.clearOptional,
            onClear: () => setPotBb(null),
            active: potCurrent == null,
          },
        }),
        customDetails(ui.sizeCustomToggle, potCustom),
      ]),
      el('p', { className: 'muted', text: ui.metaOptionalHint }),
    ]),
  )

  const blindsRow = el('div', { className: 'formRow' }, [
    el('label', { className: 'field' }, [
      el('span', { className: 'label', text: ui.sb }),
      el('input', {
        className: 'input',
        attrs: {
          type: 'number',
          min: 0,
          step: 'any',
          inputmode: 'decimal',
          value: Number.isFinite(hand.smallBlind) ? hand.smallBlind : '',
        },
        on: {
          change: (e) => {
            const parsed = parseBlindInput(
              (e.target as HTMLInputElement).value,
            )
            setBlinds({ smallBlind: parsed })
          },
        },
      }),
    ]),
    el('label', { className: 'field' }, [
      el('span', { className: 'label', text: ui.bb }),
      el('input', {
        className: 'input',
        attrs: {
          type: 'number',
          min: 0,
          step: 'any',
          inputmode: 'decimal',
          value: Number.isFinite(hand.bigBlind) ? hand.bigBlind : '',
        },
        on: {
          change: (e) => {
            const parsed = parseBlindInput(
              (e.target as HTMLInputElement).value,
            )
            // Empty BB → null/NaN via setBlinds so Next stays disabled
            setBlinds({
              bigBlind:
                parsed === 0 &&
                (e.target as HTMLInputElement).value.trim() === ''
                  ? null
                  : parsed,
            })
          },
        },
      }),
    ]),
  ])

  return el('section', { className: 'panel' }, [
    el('h2', { className: 'panelTitle', attrs: { tabindex: -1 }, text: ui.setupTitle }),
    explainOnly(el('p', { className: 'panelHint', text: ui.setupHint })),
    explainOnly(el('p', { className: 'muted setupBasicsHint', text: ui.setupBasicsHint })),
    // Softwise M8: Setup L1 = blinds only — Quiet pot / hero stack / seat-table
    // info chrome (players·stack·pot stay under precision details). Position keeps
    // full table viz for seat pick.
    el('div', { className: 'tablePreviewWrap tablePreviewSetupQuiet' }, [
      renderTableViz({
        playerCount: hand.playerCount,
        heroPosition: hand.heroPosition,
        mode: 'preview',
        compact: true,
      }),
    ]),
    el('div', { className: 'fieldGrid' }, [
      // Softwise M7: blinds is the one Setup L1; players/stack/ante/eff/pot Quiet.
      blindsRow,
      precisionDetails,
    ]),
    // Softwise M10c: setup is a side screen (opened from the defaults
    // segment) — no skip / quick-hand jumps; a quiet 돌아가기 returns.
  ])
}
